import { useState, useEffect } from 'react';
import { Plus, Search, BookOpen, GraduationCap, Calculator, Filter, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';
import { usePermissions } from '../components/Layout';

export default function GradesPage() {
  const { canCreate } = usePermissions();
  const [grades, setGrades] = useState([]);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schoolYear, setSchoolYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGradeDialog, setShowGradeDialog] = useState(false);
  const [showSubjectDialog, setShowSubjectDialog] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [formLoading, setFormLoading] = useState(false);

  const [newGrade, setNewGrade] = useState({
    student_id: '', subject_id: '', period: 'trimester1', grade_type: 'devoir',
    score: '', max_score: 20, coefficient: 1, date: new Date().toISOString().split('T')[0], teacher_id: '', notes: ''
  });

  const [newSubject, setNewSubject] = useState({ name: '', code: '', coefficient: 1 });

  useEffect(() => { fetchData(); }, [filterPeriod, filterSubject]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPeriod !== 'all') params.period = filterPeriod;
      if (filterSubject !== 'all') params.subject_id = filterSubject;

      const [gradesRes, studentsRes, subjectsRes, teachersRes, yearRes] = await Promise.all([
        api.get('/grades', { params }),
        api.get('/students', { params: { student_type: 'classical' } }),
        api.get('/subjects'),
        api.get('/teachers'),
        api.get('/school-years/active')
      ]);

      // Also get mixed students
      const mixedRes = await api.get('/students', { params: { student_type: 'mixed' } });
      
      setGrades(gradesRes.data);
      setStudents([...studentsRes.data, ...mixedRes.data]);
      setSubjects(subjectsRes.data);
      setTeachers(teachersRes.data);
      setSchoolYear(yearRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGrade = async (e) => {
    e.preventDefault();
    if (!newGrade.student_id || !newGrade.subject_id || !newGrade.score) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setFormLoading(true);
    try {
      await api.post('/grades', { ...newGrade, school_year_id: schoolYear?.id || '', score: parseFloat(newGrade.score) });
      toast.success('Note enregistrée');
      setShowGradeDialog(false);
      setNewGrade({ student_id: '', subject_id: '', period: 'trimester1', grade_type: 'devoir', score: '', max_score: 20, coefficient: 1, date: new Date().toISOString().split('T')[0], teacher_id: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.name || !newSubject.code) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setFormLoading(true);
    try {
      await api.post('/subjects', newSubject);
      toast.success('Matière créée');
      setShowSubjectDialog(false);
      setNewSubject({ name: '', code: '', coefficient: 1 });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const getStudentName = (id) => students.find(s => s.id === id)?.first_name + ' ' + students.find(s => s.id === id)?.last_name || 'Inconnu';
  const getSubjectName = (id) => subjects.find(s => s.id === id)?.name || 'Inconnu';
  const getPeriodLabel = (p) => ({ trimester1: '1er Trimestre', trimester2: '2ème Trimestre', trimester3: '3ème Trimestre', semester1: '1er Semestre', semester2: '2ème Semestre' }[p] || p);
  const getGradeTypeLabel = (t) => ({ devoir: 'Devoir', composition: 'Composition', examen: 'Examen' }[t] || t);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="grades-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Notes Classiques</h1>
          <p className="text-muted-foreground text-sm">Gérez les notes des élèves classiques et mixtes</p>
        </div>
        {canCreate('evaluations') && (
          <div className="flex gap-2">
            <Dialog open={showSubjectDialog} onOpenChange={setShowSubjectDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="add-subject-btn">
                  <Plus className="w-4 h-4 mr-2" />Matière
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nouvelle matière</DialogTitle>
                  <DialogDescription>Ajouter une matière au programme</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddSubject} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nom *</Label>
                    <Input value={newSubject.name} onChange={(e) => setNewSubject({...newSubject, name: e.target.value})} placeholder="Mathématiques" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Code *</Label>
                      <Input value={newSubject.code} onChange={(e) => setNewSubject({...newSubject, code: e.target.value})} placeholder="MATH" />
                    </div>
                    <div className="space-y-2">
                      <Label>Coefficient</Label>
                      <Input type="number" value={newSubject.coefficient} onChange={(e) => setNewSubject({...newSubject, coefficient: parseFloat(e.target.value) || 1})} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowSubjectDialog(false)}>Annuler</Button>
                    <Button type="submit" disabled={formLoading}>{formLoading ? 'Création...' : 'Créer'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={showGradeDialog} onOpenChange={setShowGradeDialog}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="add-grade-btn">
                  <Plus className="w-4 h-4 mr-2" />Nouvelle note
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Enregistrer une note</DialogTitle>
                  <DialogDescription>Saisir une note pour un élève</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddGrade} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Élève *</Label>
                    <Select value={newGrade.student_id} onValueChange={(v) => setNewGrade({...newGrade, student_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>
                        {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Matière *</Label>
                      <Select value={newGrade.subject_id} onValueChange={(v) => setNewGrade({...newGrade, subject_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                        <SelectContent>
                          {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Période *</Label>
                      <Select value={newGrade.period} onValueChange={(v) => setNewGrade({...newGrade, period: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="trimester1">1er Trimestre</SelectItem>
                          <SelectItem value="trimester2">2ème Trimestre</SelectItem>
                          <SelectItem value="trimester3">3ème Trimestre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={newGrade.grade_type} onValueChange={(v) => setNewGrade({...newGrade, grade_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="devoir">Devoir</SelectItem>
                          <SelectItem value="composition">Composition</SelectItem>
                          <SelectItem value="examen">Examen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Note *</Label>
                      <Input type="number" step="0.5" max={newGrade.max_score} value={newGrade.score} onChange={(e) => setNewGrade({...newGrade, score: e.target.value})} placeholder="15" />
                    </div>
                    <div className="space-y-2">
                      <Label>Sur</Label>
                      <Input type="number" value={newGrade.max_score} onChange={(e) => setNewGrade({...newGrade, max_score: parseFloat(e.target.value) || 20})} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" value={newGrade.date} onChange={(e) => setNewGrade({...newGrade, date: e.target.value})} />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowGradeDialog(false)}>Annuler</Button>
                    <Button type="submit" disabled={formLoading}>{formLoading ? 'Enregistrement...' : 'Enregistrer'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{grades.length}</p>
              <p className="text-xs text-muted-foreground">Notes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{subjects.length}</p>
              <p className="text-xs text-muted-foreground">Matières</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold">{grades.length > 0 ? (grades.reduce((a, g) => a + g.score, 0) / grades.length).toFixed(1) : '-'}</p>
              <p className="text-xs text-muted-foreground">Moyenne générale</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{students.length}</p>
              <p className="text-xs text-muted-foreground">Élèves</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={filterPeriod} onValueChange={setFilterPeriod}>
              <SelectTrigger className="w-full sm:w-48 rounded-full">
                <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Période" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les périodes</SelectItem>
                <SelectItem value="trimester1">1er Trimestre</SelectItem>
                <SelectItem value="trimester2">2ème Trimestre</SelectItem>
                <SelectItem value="trimester3">3ème Trimestre</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterSubject} onValueChange={setFilterSubject}>
              <SelectTrigger className="w-full sm:w-48 rounded-full">
                <BookOpen className="w-4 h-4 mr-2" /><SelectValue placeholder="Matière" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les matières</SelectItem>
                {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : grades.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-4">Aucune note enregistrée</p>
              <Button variant="outline" className="rounded-full" onClick={() => setShowGradeDialog(true)}>Ajouter une note</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Élève</TableHead>
                  <TableHead>Matière</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-center">Note</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grades.map((grade) => (
                  <TableRow key={grade.id} className="table-row-hover">
                    <TableCell className="font-medium">{getStudentName(grade.student_id)}</TableCell>
                    <TableCell>{getSubjectName(grade.subject_id)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{getPeriodLabel(grade.period)}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{getGradeTypeLabel(grade.grade_type)}</TableCell>
                    <TableCell className="text-center"><Badge className="bg-primary">{grade.score}/{grade.max_score}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{grade.date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
